import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { MobileNav } from "@/components/layout/MobileNav";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import ProviderProfile from "@/pages/ProviderProfile";
import HealthAI from "@/pages/HealthAI";
import JoinDoctor from "@/pages/JoinDoctor";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import ComingSoon from "@/pages/ComingSoon";
import Checkout from "@/pages/Checkout";
import Insurance from "@/pages/Insurance";
import AuthCallback from "@/pages/AuthCallback";
import { CartProvider } from "@/context/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { useEffect } from "react";

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-[60vh] grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Shell() {
  const loc = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-white text-[#020B18]">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1 pb-20 md:pb-0">
        <AnimatePresence mode="wait">
          <Routes location={loc} key={loc.pathname}>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<Search />} />
            <Route path="/provider/:id" element={<ProviderProfile />} />
            <Route path="/healthai" element={<HealthAI />} />
            <Route path="/join-doctor" element={<JoinDoctor />} />
            <Route path="/shop" element={<ComingSoon kind="shop" />} />
            <Route path="/diagnostics" element={<ComingSoon kind="diagnostics" />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/insurance" element={<Insurance />} />
            <Route path="/wellness-mart" element={<ComingSoon kind="wellness" />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
      <MobileNav />
      <CartDrawer />
      <FloatingWhatsApp />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <CartProvider><Shell /></CartProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
